import { useState } from "react";
import { Plus, Pencil } from "lucide-react";
import { trpc } from "../lib/trpc";
import { useLanguage } from "../contexts/LanguageContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input, Select } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  REGIONS,
  SERVICE_TYPES,
  SERVICE_TYPE_META,
  VEHICLE_TYPES,
  type Region,
  type ServiceType,
} from "../../../shared/const";

type Rule = {
  id: number;
  region: "taiwan" | "hongkong";
  serviceType: ServiceType;
  vehicleType: string;
  basePrice: string;
  currency: "TWD" | "HKD" | "USD";
};

const regionLabel = (r: string) =>
  r === "taiwan"
    ? { "zh-TW": "台灣", "en-US": "Taiwan", "zh-CN": "台湾" }
    : { "zh-TW": "香港", "en-US": "Hong Kong", "zh-CN": "香港" };

const vehicleLabel = (v: string) => VEHICLE_TYPES.find((x) => x.value === v)?.label ?? { "zh-TW": v, "en-US": v, "zh-CN": v };

export default function PricingRules() {
  const { t } = useLanguage();
  const rulesQ = trpc.pricing.listRules.useQuery();
  const rules = (rulesQ.data ?? []) as Rule[];

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-medium tracking-tight">
            {t({ "zh-TW": "計價規則", "en-US": "Pricing Rules", "zh-CN": "计价规则" })}
          </h1>
          <p className="text-[13px] text-muted-foreground">
            {t({
              "zh-TW": "依區域 × 服務類型 × 車型設定基礎價。未設定者，估價時自動回退內建預設。",
              "en-US": "Base price per region × service type × vehicle. Unset combos fall back to built-in defaults.",
              "zh-CN": "依区域 × 服务类型 × 车型设定基础价。未设定者，估价时自动回退内建预设。",
            })}
          </p>
        </div>
        <RuleDialog onDone={() => rulesQ.refetch()} />
      </div>

      <Card>
        <CardContent className="p-0">
          {rulesQ.isLoading ? (
            <div className="h-24 animate-pulse rounded-lg bg-muted/40" />
          ) : rules.length === 0 ? (
            <p className="p-8 text-center text-[13px] text-muted-foreground">
              {t({
                "zh-TW": "尚無計價規則，估價暫時使用內建預設表。新增規則後即以此為準。",
                "en-US": "No rules yet — estimates use built-in defaults. Add a rule to override.",
                "zh-CN": "尚无计价规则，估价暂时使用内建预设表。新增规则后即以此为准。",
              })}
            </p>
          ) : (
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-border text-left font-mono text-[12px] uppercase tracking-wider text-muted-foreground">
                  <th className="p-3">{t({ "zh-TW": "區域", "en-US": "Region", "zh-CN": "区域" })}</th>
                  <th className="p-3">{t({ "zh-TW": "服務類型", "en-US": "Service", "zh-CN": "服务类型" })}</th>
                  <th className="p-3">{t({ "zh-TW": "車型", "en-US": "Vehicle", "zh-CN": "车型" })}</th>
                  <th className="p-3 text-right">{t({ "zh-TW": "基礎價", "en-US": "Base price", "zh-CN": "基础价" })}</th>
                  <th className="p-3 text-right">{t({ "zh-TW": "操作", "en-US": "Action", "zh-CN": "操作" })}</th>
                </tr>
              </thead>
              <tbody>
                {rules.map((r) => (
                  <tr key={r.id} className="border-b border-border/60 last:border-0">
                    <td className="p-3">{t(regionLabel(r.region))}</td>
                    <td className="p-3">{t(SERVICE_TYPE_META[r.serviceType].label)}</td>
                    <td className="p-3">{t(vehicleLabel(r.vehicleType))}</td>
                    <td className="p-3 text-right numeric font-medium">
                      {r.currency} {Number(r.basePrice).toLocaleString()}
                    </td>
                    <td className="p-3 text-right">
                      <RuleDialog rule={r} onDone={() => rulesQ.refetch()} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function RuleDialog({ rule, onDone }: { rule?: Rule; onDone: () => void }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [region, setRegion] = useState<Region>(rule?.region ?? "taiwan");
  const [serviceType, setServiceType] = useState<ServiceType>(rule?.serviceType ?? "airport_pickup");
  const [vehicleType, setVehicleType] = useState(rule?.vehicleType ?? "sedan");
  const [basePrice, setBasePrice] = useState(rule ? Number(rule.basePrice) : 0);
  const currency: "TWD" | "HKD" = region === "taiwan" ? "TWD" : "HKD";

  const m = trpc.pricing.upsertRule.useMutation({
    onSuccess: () => {
      setOpen(false);
      onDone();
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {rule ? (
          <Button variant="ghost" size="sm">
            <Pencil className="h-3.5 w-3.5" />
            {t({ "zh-TW": "編輯", "en-US": "Edit", "zh-CN": "编辑" })}
          </Button>
        ) : (
          <Button size="sm">
            <Plus className="h-3.5 w-3.5" />
            {t({ "zh-TW": "新增規則", "en-US": "New rule", "zh-CN": "新增规则" })}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {rule
              ? t({ "zh-TW": "編輯計價規則", "en-US": "Edit pricing rule", "zh-CN": "编辑计价规则" })
              : t({ "zh-TW": "新增計價規則", "en-US": "New pricing rule", "zh-CN": "新增计价规则" })}
          </DialogTitle>
          <DialogDescription>
            {t({ "zh-TW": "幣別依區域自動決定（台灣 TWD、香港 HKD）。", "en-US": "Currency follows region (TW = TWD, HK = HKD).", "zh-CN": "币别依区域自动决定（台湾 TWD、香港 HKD）。" })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t({ "zh-TW": "區域", "en-US": "Region", "zh-CN": "区域" })}</Label>
              <Select value={region} onChange={(e) => setRegion(e.target.value as Region)}>
                {REGIONS.map((r) => (
                  <option key={r} value={r}>
                    {t(regionLabel(r))}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t({ "zh-TW": "服務類型", "en-US": "Service", "zh-CN": "服务类型" })}</Label>
              <Select value={serviceType} onChange={(e) => setServiceType(e.target.value as ServiceType)}>
                {SERVICE_TYPES.map((s) => (
                  <option key={s} value={s}>
                    {t(SERVICE_TYPE_META[s].label)}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t({ "zh-TW": "車型", "en-US": "Vehicle", "zh-CN": "车型" })}</Label>
              <Select value={vehicleType} onChange={(e) => setVehicleType(e.target.value)}>
                {VEHICLE_TYPES.map((v) => (
                  <option key={v.value} value={v.value}>
                    {t(v.label)}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>
                {t({ "zh-TW": "基礎價", "en-US": "Base price", "zh-CN": "基础价" })}（{currency}）
              </Label>
              <Input type="number" min={1} value={basePrice} onChange={(e) => setBasePrice(Number(e.target.value))} />
            </div>
          </div>
          {serviceType === "charter" && (
            <p className="text-[12px] text-muted-foreground">
              {t({ "zh-TW": "包車為每小時單價，估價時乘以時數。", "en-US": "Charter base price is per hour.", "zh-CN": "包车为每小时单价，估价时乘以时数。" })}
            </p>
          )}
          {m.error && <p className="text-[12px] text-destructive">{m.error.message}</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            {t({ "zh-TW": "取消", "en-US": "Cancel", "zh-CN": "取消" })}
          </Button>
          <Button
            onClick={() => m.mutate({ id: rule?.id, region, serviceType, vehicleType, basePrice, currency })}
            disabled={m.isPending || basePrice <= 0}
          >
            {t({ "zh-TW": "儲存", "en-US": "Save", "zh-CN": "保存" })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
